/**
 * Die Karte. Kapitel D der Spec.
 *
 * Gezeichnet wird direkt auf dem Luftbild, mit Fang auf rechte Winkel — Daecher
 * sind fast immer rechtwinklig, und ohne Fang wird das Nachzeichnen muehsam und
 * ungenau (PV-D06).
 *
 * Bewusst ohne Zeichenbibliothek: Wir brauchen genau vier Handgriffe (Punkt
 * setzen, Punkt schieben, Kante waehlen, Modul antippen), und die Rechnung
 * dahinter steht ohnehin im Geometriekern.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import * as maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import kartenworker from 'maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url';

// MapLibre 6 lädt seinen Worker als eigene Datei, gesucht neben dem Modul.
// Vite legt das Modul beim Vorbereiten woanders hin, und dann fand die Karte
// den Worker nicht — sie blieb leer („Worker failed to load“). Ausdrücklich
// angegeben, kopiert Vite die Datei mit, und der Service Worker lädt sie vor.
maplibregl.setWorkerUrl(kartenworker);

import {
  fuegeMitFangAn,
  massendstriche,
  naechsteKante,
  pfeilspitze,
  punktInnerhalb,
  flaecheninhalt,
  streckeM,
  type Geo,
} from '@pv/geometrie';

import type { Flaeche, Kartenmarke, Kartenmarkenart } from '../typen.ts';
import type { Planung } from '../planung.ts';
import { platzSchluessel } from '../planung.ts';

export interface Kartenquelle {
  schluessel: string;
  name: string;
  url: string;
  maxZoom: number;
  aufloesungCm?: number;
  nachweis: string;
  lizenz: string;
}

export type Kartenmodus =
  | 'ansehen'
  | 'zeichnen'
  | 'aussparung'
  | 'modulfeld'
  | 'traufkante'
  | 'messen'
  | 'vorschlag'
  | 'fassade'
  | 'verstringen'
  | 'verschattung'
  | 'verschattung-zeichnen'
  | 'bearbeiten'
  | 'umrisse'
  | 'module'
  | 'bemassung'
  | 'markierung';

export const MARKENNAMEN: Record<Kartenmarkenart, string> = {
  bemassung: 'Bemaßung',
  wechselrichter: 'Wechselrichter',
  verteiler: 'Verteiler',
  hinweis: 'Hinweis',
};

/**
 * Gelb für Maße wie auf einem Plan; die Geräte in den Farben, die sie auch im
 * Stringplan haben. Alle mit dunkler Fassung, damit sie auf hellen wie
 * dunklen Dächern lesbar bleiben.
 */
const MARKENFARBEN: Record<Kartenmarkenart, string> = {
  bemassung: '#f5c400',
  wechselrichter: '#2f86c8',
  verteiler: '#7b4fc9',
  hinweis: '#d9822b',
};

/** Länge, wie sie am Schirm steht: unter einem Meter in Zentimetern. */
const masstext = (laenge: number): string =>
  laenge < 1 ? `${(laenge * 100).toFixed(0)} cm` : `${laenge.toFixed(2).replace('.', ',')} m`;

/** Meter je Bildpunkt bei dieser Zoomstufe (MapLibre rechnet mit 512er Kacheln). */
const meterJePixel = (lat: number, zoom: number): number =>
  (40075016.686 * Math.cos((lat * Math.PI) / 180)) / (512 * 2 ** zoom);

/** In diesen Modi wird ein Polygon aufgezogen. */
const ZEICHENMODI: Kartenmodus[] = ['zeichnen', 'aussparung', 'modulfeld', 'fassade', 'verschattung-zeichnen'];

/**
 * Wie viele Zoomstufen ueber die Kachelquelle hinaus. Das Luftbild wird dabei
 * hochskaliert und unscharf — gewollt: Zum genauen Setzen einer Ecke zaehlt,
 * wie gross die Sache am Schirm ist, nicht wie scharf. Vier Stufen sind das
 * Sechzehnfache.
 *
 * Wichtig ist die Trennung: Die Quelle bleibt bei ihrer echten Hoechststufe
 * (sonst holt MapLibre Kacheln, die es nicht gibt), nur die Karte darf weiter.
 */
const ZUSAETZLICHE_ZOOMSTUFEN = 4;

interface Eigenschaften {
  mittelpunkt: Geo;
  /**
   * Hierhin zoomen, sobald ein neuer Ring kommt. Ein Kamin ist bei
   * Dachansicht zehn Bildpunkte groß — die Eckgriffe verdecken ihn ganz, und
   * zum Verschieben bleibt nichts zum Anfassen.
   */
  zoomAuf: Geo[] | null;
  quelle: Kartenquelle;
  flaechen: Flaeche[];
  planungen: Map<string, Planung>;
  aktiveFlaecheId: string | null;
  modus: Kartenmodus;
  /** Vorgeladene Gebaeudeumrisse — zeigt, wo ein Vorschlag moeglich ist. */
  gebaeudeumrisse: Geo[][];
  /**
   * Woher die Umrisse stammen. Der Kataster steht unter CC BY — die Quelle
   * muss genannt werden, wo die Daten zu sehen sind.
   */
  umrissquelle: 'bev' | 'osm' | null;
  /** Im Modus "umrisse": verschiebt das Ziehen alle Umrisse gemeinsam. */
  onUmrisseVerschoben: (dLat: number, dLng: number) => void;
  /** Welcher Modulplatz gehoert zu welchem String (PV-H02). */
  modulfarben: Map<string, string>;
  /** Die Linien von Modul zu Modul, in Stringfarbe. */
  stringlinien: Array<{ farbe: string; punkte: Geo[] }>;
  /** Sparren und Pfetten, durchscheinend über dem Dach. */
  traeger: Array<{ art: 'sparren' | 'pfetten'; punkte: Geo[] }>;
  /** Gezeichnete Modulfelder; das der gewählten Fläche kräftiger. */
  modulfelder: Array<{ flaecheId: string; ring: Geo[] }>;
  /** Die Kante, auf die gerade eingemessen wird — damit man sieht, welche (PV-D05). */
  hervorgehobeneKante: Geo[] | null;
  /** Das Hindernis, dessen Maße rechts gerade eingetragen werden. */
  gewaehltesHindernis: { flaecheId: string; index: number } | null;
  /** Im Modus "bearbeiten": die Ecken, die gezogen werden koennen. */
  bearbeitung: Geo[] | null;
  /**
   * `laufend`: ein weiterer Schritt desselben Zugs. Ein Zug ist ein Eintrag
   * im Verlauf — vorher war es jedes Bewegungsereignis, und „Zurück“ bewegte
   * die Ecke um einen Bildpunkt.
   */
  onBearbeitung: (ring: Geo[], laufend?: boolean) => void;
  /** Welche Ecke ist angetippt — sie kann dann entfernt werden. */
  gewaehlteEcke: number | null;
  onEckeGewaehlt: (index: number | null) => void;
  /** Verschattungsobjekte und ihr Schattenwurf (Kapitel N). */
  verschattungsobjekte: Geo[][];
  schattenwuerfe: Geo[][];
  entwurf: Geo[];
  onEntwurf: (punkte: Geo[]) => void;
  onFlaecheGewaehlt: (id: string) => void;
  onKanteGewaehlt: (index: number) => void;
  onModulGetippt: (flaecheId: string, schluessel: string) => void;
  /** Beim Verstringen über Module gewischt — in der Reihenfolge des Wischens. */
  onModuleGewischt: (module: Array<{ flaecheId: string; schluessel: string }>) => void;
  /** Farbe des Strings, in den gerade gewischt wird. */
  wischfarbe: string | null;
  onGemessen: (laengeM: number | null) => void;
  onGebaeudeGetippt: (punkt: Geo) => void;
  /** Bemaßungen und Markierungen (ohne die, die gerade bearbeitet wird). */
  kartenmarken: Kartenmarke[];
  /** Was gerade aufgezogen wird — Art und Text, die Punkte sind der Entwurf. */
  entwurfsmarke: { art: Kartenmarkenart; text: string } | null;
  /** Montage: Beschriftungen sind zu sehen, aber nicht anzutippen. */
  markenWaehlbar: boolean;
  onKartenmarkeGetippt: (id: string) => void;
  /** Wahre Länge, wenn beide Enden auf einer Dachfläche liegen (`dachstrecke`). */
  dachlaenge: (a: Geo, b: Geo) => number | null;
}

const leer = { type: 'FeatureCollection', features: [] } as GeoJSON.FeatureCollection;

/**
 * Wie weit ein Tipp neben einer Ecke noch als Treffer zaehlt, in Bildpunkten.
 * Ein Finger deckt einen Fleck von gut einem Zentimeter ab; der Eckpunkt hat
 * neun Pixel Radius. Exakt unter dem Finger traf man ihn kaum.
 */
const fangRadius = (): number =>
  window.matchMedia?.('(pointer: coarse)').matches ? 24 : 10;

/**
 * Was zuletzt in jede Quelle geschrieben wurde. Treffer beim Antippen werden
 * daraus gerechnet, nicht über queryRenderedFeatures: Unter MapLibre 6
 * liefert die Abfrage während einer Berührung nichts, und am Tablet ließen
 * sich weder Ecken ziehen noch Module wischen (11/2026).
 */
const QUELLDATEN = new WeakMap<maplibregl.Map, Map<string, GeoJSON.FeatureCollection>>();

/** Welche Quelle und welche Punkte eine Ebene zeigt — wie die Filter der Ebenen. */
const PUNKTEBENEN: Record<string, { quelle: string; art: string }> = {
  'bearbeitung-ecken': { quelle: 'bearbeitung', art: 'ecke' },
  'bearbeitung-mitten': { quelle: 'bearbeitung', art: 'mitte' },
};

/** Der Punkt einer Ebene, der dem Tipp am nächsten liegt — im Fangradius. */
function naechstesIm(
  m: maplibregl.Map,
  punkt: maplibregl.Point,
  ebene: string,
): GeoJSON.Feature<GeoJSON.Point> | undefined {
  const art = PUNKTEBENEN[ebene];
  if (!art) return undefined;
  const daten = QUELLDATEN.get(m)?.get(art.quelle);
  const r = fangRadius();
  let bester: GeoJSON.Feature<GeoJSON.Point> | undefined;
  let besterAbstand = Infinity;
  for (const f of daten?.features ?? []) {
    if (f.geometry.type !== 'Point' || f.properties?.['art'] !== art.art) continue;
    const [lng, lat] = f.geometry.coordinates as [number, number];
    const p = m.project([lng, lat]);
    const d = Math.hypot(p.x - punkt.x, p.y - punkt.y);
    if (d <= r && d < besterAbstand) {
      besterAbstand = d;
      bester = f as GeoJSON.Feature<GeoJSON.Point>;
    }
  }
  return bester;
}

export function Karte(eigenschaften: Eigenschaften): React.JSX.Element {
  const {
    mittelpunkt,
    zoomAuf,
    quelle,
    flaechen,
    planungen,
    aktiveFlaecheId,
    modus,
    gebaeudeumrisse,
    umrissquelle,
    modulfarben,
    stringlinien,
    traeger,
    modulfelder,
    wischfarbe,
    verschattungsobjekte,
    schattenwuerfe,
    entwurf,
    hervorgehobeneKante,
    gewaehltesHindernis,
    bearbeitung,
    gewaehlteEcke,
    onEntwurf,
    onFlaecheGewaehlt,
    onKanteGewaehlt,
    onModulGetippt,
    onGemessen,
    kartenmarken,
    entwurfsmarke,
    markenWaehlbar,
    onKartenmarkeGetippt,
    dachlaenge,
  } = eigenschaften;
  // onGebaeudeGetippt wird nur im Klickhandler gebraucht und laeuft dort ueber
  // die Referenz unten.

  const behaelter = useRef<HTMLDivElement>(null);
  const karte = useRef<maplibregl.Map | null>(null);
  const [bereit, setBereit] = useState(false);
  const [messpunkte, setMesspunkte] = useState<Geo[]>([]);
  /**
   * Wo das Mass am Bildschirm steht. Die Karte hat bewusst keine Schriftquelle
   * (glyphs) — ein Schriftpaket vom Netz nachzuladen hiesse, dass die
   * Beschriftung ohne Empfang fehlt. Deshalb liegt sie als gewoehnliches
   * Element ueber der Karte und wird bei jeder Bewegung nachgerechnet.
   */
  const [massmarke, setMassmarke] = useState<{ x: number; y: number; text: string } | null>(null);
  /** Die Schilder an Bemaßungen und Markierungen — aus demselben Grund als Element. */
  const [schilder, setSchilder] = useState<
    Array<{ id: string; x: number; y: number; text: string; farbe: string }>
  >([]);
  /** Die Pfeilspitzen sollen am Schirm gleich groß bleiben — dafür die Zoomstufe. */
  const [zoom, setZoom] = useState(19);

  // Die Rueckrufe aendern sich bei jedem Rendern. Damit der Klickhandler nicht
  // jedes Mal neu gehaengt werden muss, laeuft er ueber eine Referenz.
  const stand = useRef(eigenschaften);
  /** Wann zuletzt ein Modul per Finger angetippt wurde — siehe Wischen. */
  const fingertippAm = useRef(0);
  stand.current = eigenschaften;
  const messstand = useRef(messpunkte);
  messstand.current = messpunkte;

  // --- Karte aufbauen ------------------------------------------------------
  useEffect(() => {
    if (!behaelter.current || karte.current) return;
    const m = new maplibregl.Map({
      container: behaelter.current,
      center: [mittelpunkt.lng, mittelpunkt.lat],
      zoom: 19,
      maxZoom: quelle.maxZoom + ZUSAETZLICHE_ZOOMSTUFEN,
      attributionControl: false,
      // Nicht drehen und nicht kippen. Beim Zoomen mit zwei Fingern drehte die
      // Karte leicht mit, und ohne Kompass gab es keinen Weg zurueck nach
      // Norden — auf einem Luftbild, auf dem nach Dachkanten gezeichnet wird.
      dragRotate: false,
      pitchWithRotate: false,
      touchPitch: false,
      style: {
        version: 8,
        sources: {
          luftbild: {
            type: 'raster',
            tiles: [quelle.url],
            tileSize: 256,
            maxzoom: quelle.maxZoom,
            attribution: quelle.nachweis,
          },
        },
        layers: [{ id: 'luftbild', type: 'raster', source: 'luftbild' }],
      },
    });
    m.touchZoomRotate.disableRotation();
    m.keyboard.disableRotation();
    m.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');

    // Bewusst 'style.load' und nicht 'load': 'load' wartet darauf, dass auch die
    // Kacheln geladen sind. Ohne Empfang und ohne vorgeladene Kacheln kaeme das
    // nie — und die Karte bliebe stumm, obwohl gezeichnet werden koennte. Das
    // Zeichnen braucht kein Luftbild, nur Koordinaten.
    const einrichten = (): void => {
      legeEbenenAn(m);
      setBereit(true);
    };
    if (m.isStyleLoaded()) einrichten();
    else m.once('style.load', einrichten);
    m.on('zoomend', () => setZoom(m.getZoom()));
    karte.current = m;
    // Nur in der Entwicklung: die Karte für die Fehlersuche im Browser.
    if (import.meta.env.DEV) (window as unknown as { __karte?: maplibregl.Map }).__karte = m;
    return () => {
      m.remove();
      karte.current = null;
      setBereit(false);
    };
    // Absichtlich nur beim ersten Mal. Ein Quellenwechsel wird unten behandelt.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Der Adresse folgen --------------------------------------------------
  // Die Karte bekam ihren Mittelpunkt nur beim Aufbau. Wer danach eine Adresse
  // suchte, sah den Knopf "Zum Objekt springen" wirken — aber die Karte blieb
  // stehen, wo sie war. Erst beim naechsten Oeffnen des Projekts stimmte der
  // Ausschnitt. Deshalb wird der Mittelpunkt jetzt nachgezogen, sobald er sich
  // wirklich aendert; ein blosses Neuzeichnen reisst die Karte nicht herum,
  // sonst koennte man nicht mehr verschieben.
  const angefahren = useRef<string | null>(null);
  useEffect(() => {
    const m = karte.current;
    if (!m || !bereit) return;
    const marke = `${mittelpunkt.lat.toFixed(6)},${mittelpunkt.lng.toFixed(6)}`;
    if (angefahren.current === marke) return;
    if (angefahren.current !== null) m.flyTo({ center: [mittelpunkt.lng, mittelpunkt.lat] });
    angefahren.current = marke;
  }, [mittelpunkt.lat, mittelpunkt.lng, bereit]);

  useEffect(() => {
    const m = karte.current;
    if (!m || !bereit || !zoomAuf || zoomAuf.length === 0) return;
    const lats = zoomAuf.map((p) => p.lat);
    const lngs = zoomAuf.map((p) => p.lng);
    m.fitBounds(
      [
        [Math.min(...lngs), Math.min(...lats)],
        [Math.max(...lngs), Math.max(...lats)],
      ],
      // Unten sitzt das Arbeitsmenue — dort mehr Rand.
      { padding: { top: 140, bottom: 240, left: 140, right: 140 }, maxZoom: m.getMaxZoom() - 1 },
    );
  }, [zoomAuf, bereit]);

  // --- Kartengrundlage umschalten (PV-D01) ---------------------------------
  useEffect(() => {
    const m = karte.current;
    if (!m || !bereit) return;
    const src = m.getSource('luftbild') as maplibregl.RasterTileSource | undefined;
    if (src) src.setTiles([quelle.url]);
    m.setMaxZoom(quelle.maxZoom + ZUSAETZLICHE_ZOOMSTUFEN);
  }, [quelle, bereit]);

  // --- Klicks --------------------------------------------------------------
  const beiKlick = useCallback((ereignis: maplibregl.MapMouseEvent) => {
    const m = karte.current;
    if (!m) return;
    const jetzt = stand.current;
    const punkt: Geo = { lat: ereignis.lngLat.lat, lng: ereignis.lngLat.lng };

    if (ZEICHENMODI.includes(jetzt.modus)) {
      // Bei der Fassade wird eine Linie aufgezogen, kein Polygon — der Fang auf
      // rechte Winkel wuerde die Wandrichtung verfaelschen.
      jetzt.onEntwurf(
        jetzt.modus === 'fassade'
          ? [...jetzt.entwurf, punkt].slice(-2)
          : fuegeMitFangAn(jetzt.entwurf, punkt),
      );
      return;
    }

    if (jetzt.modus === 'bearbeiten') {
      // Ein Tipp auf die Kantenmitte setzt dort eine neue Ecke. Das Ziehen
      // einer bestehenden laeuft ueber mousedown/touchstart, nicht hier.
      const ecke = [naechstesIm(m, ereignis.point, 'bearbeitung-ecken')].filter(Boolean) as GeoJSON.Feature[];
      const index = ecke[0]?.properties?.['index'];
      if (typeof index === 'number') {
        jetzt.onEckeGewaehlt(index);
        return;
      }
      const treffer = [naechstesIm(m, ereignis.point, 'bearbeitung-mitten')].filter(Boolean) as GeoJSON.Feature[];
      const nach = treffer[0]?.properties?.['nach'];
      if (typeof nach === 'number' && jetzt.bearbeitung) {
        const ring = [...jetzt.bearbeitung];
        ring.splice(nach + 1, 0, punkt);
        jetzt.onBearbeitung(ring);
        jetzt.onEckeGewaehlt(nach + 1);
      }
      return;
    }

    if (jetzt.modus === 'vorschlag') {
      jetzt.onGebaeudeGetippt(punkt);
      return;
    }

    // Bemaßung und Markierung: zwei Punkte, ein dritter Tipp beginnt neu — wie
    // beim Messen. Eine Bemaßung rastet auf Ecken von Flächen und Aussparungen
    // ein; gemessen wird fast immer von Ecke zu Ecke.
    if (jetzt.modus === 'bemassung' || jetzt.modus === 'markierung') {
      const gesetzt =
        jetzt.modus === 'bemassung' ? (fangeEcke(m, ereignis.point, jetzt.flaechen) ?? punkt) : punkt;
      jetzt.onEntwurf(jetzt.entwurf.length >= 2 ? [gesetzt] : [...jetzt.entwurf, gesetzt]);
      return;
    }

    if (jetzt.modus === 'messen') {
      const neu = messstand.current.length >= 2 ? [punkt] : [...messstand.current, punkt];
      setMesspunkte(neu);
      jetzt.onGemessen(neu.length === 2 ? streckeM(neu[0]!, neu[1]!) : null);
      return;
    }

    if (jetzt.modus === 'traufkante') {
      const flaeche = jetzt.flaechen.find((f) => f.id === jetzt.aktiveFlaecheId);
      if (!flaeche || flaeche.polygon.length < 3) return;
      jetzt.onKanteGewaehlt(naechsteKante(flaeche.polygon, punkt));
      return;
    }

    // Ansehen und Verstringen: erst Module, dann Flaechen.
    //
    // Die entfernten Plaetze sind absichtlich mit dabei — sonst gibt es keinen
    // Weg zurueck. Wer ein Modul wegtippt, tippt auf denselben Fleck und hat
    // es wieder; ohne diese Ebene half nur "alle zuruecksetzen".
    //
    // Gerechnet aus den Daten, nicht über queryRenderedFeatures: Unter
    // MapLibre 6 liefert die Abfrage während einer Berührung nichts, und ein
    // Fingertipp wird beim Loslassen ausgewertet (iPad-Simulator, 11/2026).
    const p = { x: punkt.lng, y: punkt.lat };
    const innen = (geo: Geo[] | undefined): boolean =>
      !!geo && geo.length >= 3 && punktInnerhalb(p, geo.map((g) => ({ x: g.lng, y: g.lat })));
    let schluessel: string | undefined;
    let flaecheDesModuls: string | undefined;
    for (const [flaecheId, planung] of jetzt.planungen) {
      const i = planung.gesetzt.findIndex((_, k) => innen(planung.moduleGeo[k]));
      const j = i >= 0 ? -1 : planung.entfernte.findIndex((_, k) => innen(planung.entfernteGeo[k]));
      const platz = i >= 0 ? planung.gesetzt[i] : j >= 0 ? planung.entfernte[j] : undefined;
      if (platz) {
        schluessel = platzSchluessel(platz);
        flaecheDesModuls = flaecheId;
        break;
      }
    }
    if (typeof schluessel === 'string' && typeof flaecheDesModuls === 'string') {
      // An- und abgeschaltet wird nur im eigenen Modus (und beim Verstringen
      // zugeordnet). Vorher nahm schon ein Tipp in der normalen Ansicht ein
      // Modul heraus — wer nur die Flaeche waehlen wollte, hatte ein Loch.
      if (jetzt.modus === 'module' || jetzt.modus === 'verstringen') {
        jetzt.onModulGetippt(flaecheDesModuls, schluessel);
      } else {
        jetzt.onFlaecheGewaehlt(flaecheDesModuls);
      }
      return;
    }
    if (jetzt.modus === 'verstringen') return;

    // Die kleinste getroffene Fläche — liegt eine in einer anderen, ist die
    // innere gemeint.
    const getroffen = jetzt.flaechen
      .filter((f) => !f.geloescht_am && innen(f.polygon))
      .sort((a, b) => flaecheninhalt(a.polygon.map((g) => ({ x: g.lng, y: g.lat }))) - flaecheninhalt(b.polygon.map((g) => ({ x: g.lng, y: g.lat }))));
    if (getroffen[0]) jetzt.onFlaecheGewaehlt(getroffen[0].id);
  }, []);

  /**
   * Fingertipps zählen beim Loslassen, nicht erst beim Klick des Browsers.
   *
   * Safari am iPad macht aus zwei schnellen Tipps nur einen Klick — beim
   * Zeichnen ging die zweite Ecke verloren (iPad-Simulator, 11/2026). Und wo
   * ein Werkzeug den Berührungsbeginn abfängt (Ecke ziehen), kommt gar kein
   * Klick. Ein kurzer Tipp ohne Bewegung wird deshalb hier ausgewertet; der
   * Klick, den der Browser danach vielleicht noch schickt, zählt nicht mehr.
   * Das Verstringen hat seinen eigenen Tipp im Wischen.
   */
  useEffect(() => {
    const m = karte.current;
    if (!m || !bereit) return;
    let start: { x: number; y: number; zeit: number } | null = null;
    const beginn = (ereignis: maplibregl.MapTouchEvent): void => {
      const finger = (ereignis.originalEvent as TouchEvent).touches.length;
      start = finger === 1 ? { x: ereignis.point.x, y: ereignis.point.y, zeit: Date.now() } : null;
    };
    const abbruch = (): void => {
      start = null;
    };
    const ende = (ereignis: maplibregl.MapTouchEvent): void => {
      const s = start;
      start = null;
      if (!s || (ereignis.originalEvent as TouchEvent).touches.length > 0) return;
      if (Date.now() - s.zeit > 500) return;
      if (Math.hypot(ereignis.point.x - s.x, ereignis.point.y - s.y) > 8) return;
      if (stand.current.modus === 'verstringen') return;
      fingertippAm.current = Date.now();
      beiKlick(ereignis as unknown as maplibregl.MapMouseEvent);
    };
    const klick = (ereignis: maplibregl.MapMouseEvent): void => {
      if (Date.now() - fingertippAm.current < 700) return;
      beiKlick(ereignis);
    };
    m.on('touchstart', beginn);
    m.on('touchend', ende);
    m.on('touchcancel', abbruch);
    m.on('click', klick);
    return () => {
      m.off('touchstart', beginn);
      m.off('touchend', ende);
      m.off('touchcancel', abbruch);
      m.off('click', klick);
    };
  }, [bereit, beiKlick]);

  useEffect(() => {
    const m = karte.current;
    if (!m || !bereit) return;
    m.getCanvas().style.cursor = modus === 'ansehen' ? '' : 'crosshair';
    if (modus !== 'messen') {
      setMesspunkte([]);
      onGemessen(null);
    }
  }, [modus, bereit, onGemessen]);

  // --- Daten in die Ebenen schreiben ---------------------------------------
  useEffect(() => {
    const m = karte.current;
    if (!m || !bereit) return;

    setzeDaten(m, 'flaechen', {
      type: 'FeatureCollection',
      features: flaechen
        .filter((f) => f.polygon.length >= 3)
        .map((f) => ({
          type: 'Feature',
          properties: { id: f.id, aktiv: f.id === aktiveFlaecheId },
          geometry: { type: 'Polygon', coordinates: [ringZuKoordinaten(f.polygon)] },
        })),
    });

    setzeDaten(m, 'sperrrand', {
      type: 'FeatureCollection',
      features: [...planungen.values()].flatMap((planung) =>
        planung.sperrrandGeo.map((ring) => ({
          type: 'Feature' as const,
          properties: {},
          geometry: { type: 'Polygon' as const, coordinates: [ringZuKoordinaten(ring)] },
        })),
      ),
    });
    setzeDaten(m, 'aussparungen', {
      type: 'FeatureCollection',
      features: flaechen
        .filter((f) => f.art === 'dach')
        .flatMap((f) =>
          f.aussparungen
            .map((ring, index) => ({ ring, index }))
            .filter(({ ring }) => ring.length >= 3)
            .map(({ ring, index }) => ({
              type: 'Feature' as const,
              properties: {
                id: f.id,
                gewaehlt: gewaehltesHindernis?.flaecheId === f.id && gewaehltesHindernis.index === index,
              },
              geometry: { type: 'Polygon' as const, coordinates: [ringZuKoordinaten(ring)] },
            })),
        ),
    });

    setzeDaten(m, 'traufkanten', {
      type: 'FeatureCollection',
      features: flaechen.flatMap((f) => {
        if (f.traufkante_index === null || f.polygon.length < 3) return [];
        const a = f.polygon[f.traufkante_index % f.polygon.length]!;
        const b = f.polygon[(f.traufkante_index + 1) % f.polygon.length]!;
        return [
          {
            type: 'Feature' as const,
            properties: { id: f.id },
            geometry: {
              type: 'LineString' as const,
              coordinates: [
                [a.lng, a.lat],
                [b.lng, b.lat],
              ],
            },
          },
        ];
      }),
    });

    const modulflaechen: GeoJSON.Feature[] = [];
    for (const [flaecheId, planung] of planungen) {
      planung.gesetzt.forEach((platz, i) => {
        const ecken = planung.moduleGeo[i];
        if (!ecken) return;
        const schluessel = platzSchluessel(platz);
        modulflaechen.push({
          type: 'Feature',
          properties: {
            flaeche: flaecheId,
            platz: schluessel,
            farbe: modulfarben.get(`${flaecheId}|${schluessel}`) ?? '#1f3a5f',
          },
          geometry: { type: 'Polygon', coordinates: [ringZuKoordinaten(ecken)] },
        });
      });
    }
    setzeDaten(m, 'module', { type: 'FeatureCollection', features: modulflaechen });

    // Dieselbe Rechnung fuer die Plaetze, die von Hand entfernt wurden.
    const entfernteFlaechen: GeoJSON.Feature[] = [];
    for (const [flaecheId, planung] of planungen) {
      planung.entfernte.forEach((platz, i) => {
        const ecken = planung.entfernteGeo[i];
        if (!ecken) return;
        entfernteFlaechen.push({
          type: 'Feature',
          properties: { flaeche: flaecheId, platz: platzSchluessel(platz) },
          geometry: { type: 'Polygon', coordinates: [ringZuKoordinaten(ecken)] },
        });
      });
    }
    setzeDaten(m, 'module-entfernt', {
      type: 'FeatureCollection',
      features: entfernteFlaechen,
    });

    setzeDaten(m, 'stringlinien', {
      type: 'FeatureCollection',
      features: stringlinien
        .filter((linie) => linie.punkte.length >= 2)
        .map((linie) => ({
          type: 'Feature' as const,
          properties: { farbe: linie.farbe },
          geometry: {
            type: 'LineString' as const,
            coordinates: ringZuKoordinaten(linie.punkte),
          },
        })),
    });
  }, [flaechen, planungen, aktiveFlaecheId, modulfarben, stringlinien, gewaehltesHindernis, bereit]);

  useEffect(() => {
    const m = karte.current;
    if (!m || !bereit) return;
    setzeDaten(m, 'traeger', {
      type: 'FeatureCollection',
      features: traeger.map((linie) => ({
        type: 'Feature' as const,
        properties: { art: linie.art },
        geometry: { type: 'LineString' as const, coordinates: ringZuKoordinaten(linie.punkte) },
      })),
    });
  }, [traeger, bereit]);

  useEffect(() => {
    const m = karte.current;
    if (!m || !bereit) return;
    setzeDaten(m, 'modulfelder', {
      type: 'FeatureCollection',
      features: modulfelder
        .filter((feld) => feld.ring.length >= 3)
        .map((feld) => ({
          type: 'Feature' as const,
          properties: { aktiv: feld.flaecheId === aktiveFlaecheId },
          geometry: { type: 'Polygon' as const, coordinates: [ringZuKoordinaten(feld.ring)] },
        })),
    });
  }, [modulfelder, aktiveFlaecheId, bereit]);

  useEffect(() => {
    const m = karte.current;
    if (!m || !bereit) return;
    const alsFlaechen = (ringe: Geo[][]): GeoJSON.FeatureCollection => ({
      type: 'FeatureCollection',
      features: ringe
        .filter((ring) => ring.length >= 3)
        .map((ring) => ({
          type: 'Feature' as const,
          properties: {},
          geometry: { type: 'Polygon' as const, coordinates: [ringZuKoordinaten(ring)] },
        })),
    });
    setzeDaten(m, 'verschattungsobjekte', alsFlaechen(verschattungsobjekte));
    setzeDaten(m, 'schatten', alsFlaechen(schattenwuerfe));
  }, [verschattungsobjekte, schattenwuerfe, bereit]);

  useEffect(() => {
    const m = karte.current;
    if (!m || !bereit) return;
    setzeDaten(m, 'gebaeude', {
      type: 'FeatureCollection',
      features:
        modus === 'vorschlag' || modus === 'umrisse'
          ? gebaeudeumrisse
              .filter((ring) => ring.length >= 3)
              .map((ring) => ({
                type: 'Feature' as const,
                properties: {},
                geometry: { type: 'Polygon' as const, coordinates: [ringZuKoordinaten(ring)] },
              }))
          : [],
    });
  }, [gebaeudeumrisse, modus, bereit]);

  useEffect(() => {
    const m = karte.current;
    if (!m || !bereit) return;
    setzeDaten(m, 'entwurf', {
      type: 'FeatureCollection',
      features:
        entwurf.length === 0
          ? []
          : [
              {
                type: 'Feature',
                properties: {},
                geometry: {
                  type: 'LineString',
                  coordinates: ringZuKoordinaten(
                    entwurf.length > 2 ? [...entwurf, entwurf[0]!] : entwurf,
                  ),
                },
              },
              ...entwurf.map((p) => ({
                type: 'Feature' as const,
                properties: {},
                geometry: { type: 'Point' as const, coordinates: [p.lng, p.lat] },
              })),
            ],
    });
  }, [entwurf, bereit]);

  // --- Eingemessene Kante hervorheben --------------------------------------
  useEffect(() => {
    const m = karte.current;
    if (!m || !bereit) return;
    setzeDaten(m, 'referenzkante', {
      type: 'FeatureCollection',
      features:
        hervorgehobeneKante && hervorgehobeneKante.length === 2
          ? [
              {
                type: 'Feature',
                properties: {},
                geometry: {
                  type: 'LineString',
                  coordinates: ringZuKoordinaten(hervorgehobeneKante),
                },
              },
            ]
          : [],
    });
  }, [hervorgehobeneKante, bereit]);

  // --- Ecken zum Ziehen ----------------------------------------------------
  useEffect(() => {
    const m = karte.current;
    if (!m || !bereit) return;
    if (!bearbeitung || bearbeitung.length < 2) {
      setzeDaten(m, 'bearbeitung', leer);
      return;
    }
    const mitten = bearbeitung.map((p, i) => {
      const q = bearbeitung[(i + 1) % bearbeitung.length]!;
      return { lat: (p.lat + q.lat) / 2, lng: (p.lng + q.lng) / 2, nach: i };
    });
    setzeDaten(m, 'bearbeitung', {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'LineString',
            coordinates: ringZuKoordinaten([...bearbeitung, bearbeitung[0]!]),
          },
        },
        ...mitten.map((p) => ({
          type: 'Feature' as const,
          properties: { art: 'mitte', nach: p.nach },
          geometry: { type: 'Point' as const, coordinates: [p.lng, p.lat] },
        })),
        ...bearbeitung.map((p, i) => ({
          type: 'Feature' as const,
          properties: { art: 'ecke', index: i, gewaehlt: i === gewaehlteEcke },
          geometry: { type: 'Point' as const, coordinates: [p.lng, p.lat] },
        })),
      ],
    });
  }, [bearbeitung, gewaehlteEcke, bereit]);

  /**
   * Die Gebaeudeumrisse als Ganzes verschieben.
   *
   * OSM und das Luftbild sind nicht deckungsgleich: Die Umrisse stammen oft aus
   * aelteren Aufnahmen mit eigenem Versatz, das Orthofoto zeigt zudem das Dach
   * und nicht den Grundriss — ein Dachvorsprung allein macht schon einen halben
   * Meter. Der Versatz ist in einer Ortschaft ueberall derselbe, also wird er
   * einmal eingestellt und gilt dann fuer alle.
   */
  useEffect(() => {
    const m = karte.current;
    if (!m || !bereit || modus !== 'umrisse') return;

    let von: maplibregl.LngLat | null = null;
    const finger = (e: maplibregl.MapMouseEvent | maplibregl.MapTouchEvent): number =>
      'touches' in e.originalEvent ? (e.originalEvent as TouchEvent).touches.length : 1;
    const beginn = (e: maplibregl.MapMouseEvent | maplibregl.MapTouchEvent): void => {
      // Mit zwei Fingern wird gezoomt, nicht ausgerichtet.
      if (finger(e) > 1) {
        ende();
        return;
      }
      e.preventDefault();
      von = e.lngLat;
      m.dragPan.disable();
      m.getCanvas().style.cursor = 'grabbing';
    };
    const bewegen = (e: maplibregl.MapMouseEvent | maplibregl.MapTouchEvent): void => {
      if (!von) return;
      if (finger(e) > 1) {
        ende();
        return;
      }
      e.preventDefault();
      stand.current.onUmrisseVerschoben(e.lngLat.lat - von.lat, e.lngLat.lng - von.lng);
      von = e.lngLat;
    };
    const ende = (): void => {
      if (!von) return;
      von = null;
      m.dragPan.enable();
      m.getCanvas().style.cursor = '';
    };

    m.on('mousedown', beginn);
    m.on('touchstart', beginn);
    m.on('mousemove', bewegen);
    m.on('touchmove', bewegen);
    m.on('mouseup', ende);
    m.on('touchend', ende);
    return () => {
      m.off('mousedown', beginn);
      m.off('touchstart', beginn);
      m.off('mousemove', bewegen);
      m.off('touchmove', bewegen);
      m.off('mouseup', ende);
      m.off('touchend', ende);
      ende();
    };
  }, [bereit, modus]);

  /**
   * Eine Ecke ziehen — mit Maus und mit dem Finger.
   *
   * Bedient wird am Tablet, also reicht 'mousedown' nicht. Solange gezogen
   * wird, ist das Verschieben der Karte abgeschaltet; sonst wandert unter dem
   * Finger die Karte statt der Ecke.
   */
  useEffect(() => {
    const m = karte.current;
    if (!m || !bereit || !bearbeitung) return;

    let gezogen: number | null = null;
    // Ziehen im Inneren verschiebt die ganze Flaeche. Gebraucht wird das
    // staendig: Die OSM-Umrisse sitzen je Gebaeude unterschiedlich daneben —
    // mal passt die Lage und die Flaeche ist zu klein, mal ist sie nur um einen
    // Meter verschoben. Ein gemeinsamer Versatz fuer alle hilft da nur im
    // Mittel, die einzelne Flaeche muss sich einzeln zurechtruecken lassen.
    let verschiebtAb: { lat: number; lng: number } | null = null;
    // Der erste Schritt eines Zugs kommt in den Verlauf, die weiteren nicht.
    let laufend = false;
    const fingerzahl = (ereignis: maplibregl.MapMouseEvent | maplibregl.MapTouchEvent): number =>
      'touches' in ereignis.originalEvent ? (ereignis.originalEvent as TouchEvent).touches.length : 1;

    const imInneren = (punkt: { lat: number; lng: number }): boolean => {
      const ring = stand.current.bearbeitung;
      if (!ring || ring.length < 3) return false;
      return punktInnerhalb(
        { x: punkt.lng, y: punkt.lat },
        ring.map((p) => ({ x: p.lng, y: p.lat })),
      );
    };

    const beginn = (ereignis: maplibregl.MapMouseEvent | maplibregl.MapTouchEvent): void => {
      // Zwei Finger zoomen — die Fläche bleibt, wo sie ist.
      if (fingerzahl(ereignis) > 1) {
        ende();
        return;
      }
      laufend = false;
      const treffer = [naechstesIm(m, ereignis.point, 'bearbeitung-ecken')].filter(Boolean) as GeoJSON.Feature[];
      const index = treffer[0]?.properties?.['index'];
      if (typeof index === 'number') {
        ereignis.preventDefault();
        gezogen = index;
      } else if (imInneren({ lat: ereignis.lngLat.lat, lng: ereignis.lngLat.lng })) {
        ereignis.preventDefault();
        verschiebtAb = { lat: ereignis.lngLat.lat, lng: ereignis.lngLat.lng };
      } else {
        return;
      }
      m.dragPan.disable();
      m.getCanvas().style.cursor = 'grabbing';
    };

    const bewegen = (ereignis: maplibregl.MapMouseEvent | maplibregl.MapTouchEvent): void => {
      if ((gezogen !== null || verschiebtAb) && fingerzahl(ereignis) > 1) {
        ende();
        return;
      }
      if (gezogen !== null) {
        ereignis.preventDefault();
        const ring = [...stand.current.bearbeitung!];
        ring[gezogen] = { lat: ereignis.lngLat.lat, lng: ereignis.lngLat.lng };
        stand.current.onBearbeitung(ring, laufend);
        laufend = true;
        return;
      }
      if (verschiebtAb) {
        ereignis.preventDefault();
        const dLat = ereignis.lngLat.lat - verschiebtAb.lat;
        const dLng = ereignis.lngLat.lng - verschiebtAb.lng;
        stand.current.onBearbeitung(
          stand.current.bearbeitung!.map((p) => ({ lat: p.lat + dLat, lng: p.lng + dLng })),
          laufend,
        );
        laufend = true;
        verschiebtAb = { lat: ereignis.lngLat.lat, lng: ereignis.lngLat.lng };
      }
    };

    const ende = (): void => {
      if (gezogen === null && !verschiebtAb) return;
      gezogen = null;
      verschiebtAb = null;
      m.dragPan.enable();
      m.getCanvas().style.cursor = '';
    };

    m.on('mousedown', beginn);
    m.on('touchstart', beginn);
    m.on('mousemove', bewegen);
    m.on('touchmove', bewegen);
    m.on('mouseup', ende);
    m.on('touchend', ende);
    return () => {
      m.off('mousedown', beginn);
      m.off('touchstart', beginn);
      m.off('mousemove', bewegen);
      m.off('touchmove', bewegen);
      m.off('mouseup', ende);
      m.off('touchend', ende);
      ende();
    };
  }, [bereit, bearbeitung !== null]);

  /**
   * Strings durch Wischen (Wunsch von Mathias, 11/2026).
   *
   * Beginnt die Bewegung auf einem Modul, wandert nicht die Karte, sondern
   * jedes ueberstrichene Modul wird vorgemerkt — gezeichnet als Spur in der
   * Stringfarbe — und beim Loslassen auf einmal uebergeben. Ein Tipp ohne
   * Bewegung bleibt ein Tipp: Dann feuert die Karte ihr `click`, und das
   * schaltet wie bisher ein einzelnes Modul.
   *
   * Zwischen zwei Bewegungsereignissen wird die Strecke abgetastet. Ein
   * schneller Strich springt sonst bei geringem Zoom ueber ein Modul hinweg,
   * das nur zehn Bildpunkte breit ist, und im String fehlt eins.
   */
  useEffect(() => {
    const m = karte.current;
    if (!m || !bereit || modus !== 'verstringen') return;

    type Treffer = { flaecheId: string; schluessel: string; mitte: [number, number]; ring: number[][] };
    let spur: Treffer[] | null = null;
    let start: maplibregl.Point | null = null;
    let zuletzt: maplibregl.Point | null = null;
    let bewegt = false;
    let mitFinger = false;

    // Welches Modul liegt unter dem Finger? Selbst gerechnet aus der Lage der
    // Module, nicht über queryRenderedFeatures: Unter MapLibre 6 liefert die
    // Abfrage während einer Berührung nichts — mit der Maus ging das Wischen,
    // mit dem Finger verschob sich die Karte (11/2026). Gerechnet in Länge und
    // Breite; für Rechtecke von einem Meter reicht das allemal.
    const modulBei = (punkt: maplibregl.Point): Treffer | null => {
      const ort = m.unproject(punkt);
      const p = { x: ort.lng, y: ort.lat };
      for (const [flaecheId, planung] of stand.current.planungen) {
        for (let i = 0; i < planung.gesetzt.length; i++) {
          const geo = planung.moduleGeo[i];
          if (!geo || geo.length < 3) continue;
          if (!punktInnerhalb(p, geo.map((g) => ({ x: g.lng, y: g.lat })))) continue;
          const ring = [...geo, geo[0]!].map((g) => [g.lng, g.lat]);
          const mitte: [number, number] = [
            geo.reduce((a, g) => a + g.lng, 0) / geo.length,
            geo.reduce((a, g) => a + g.lat, 0) / geo.length,
          ];
          return { flaecheId, schluessel: platzSchluessel(planung.gesetzt[i]!), mitte, ring };
        }
      }
      return null;
    };

    const zeichne = (): void => {
      const liste = spur ?? [];
      setzeDaten(m, 'wisch', {
        type: 'FeatureCollection',
        features: [
          ...liste.map((t) => ({
            type: 'Feature' as const,
            properties: {},
            geometry: { type: 'Polygon' as const, coordinates: [t.ring] },
          })),
          ...(liste.length >= 2
            ? [
                {
                  type: 'Feature' as const,
                  properties: {},
                  geometry: { type: 'LineString' as const, coordinates: liste.map((t) => t.mitte) },
                },
              ]
            : []),
        ],
      });
    };

    const merke = (punkt: maplibregl.Point): void => {
      if (!spur) return;
      const t = modulBei(punkt);
      if (!t) return;
      const letzter = spur[spur.length - 1];
      if (letzter && letzter.flaecheId === t.flaecheId && letzter.schluessel === t.schluessel) return;
      if (spur.some((s) => s.flaecheId === t.flaecheId && s.schluessel === t.schluessel)) return;
      spur.push(t);
      zeichne();
    };

    const fingerzahl = (ereignis: maplibregl.MapMouseEvent | maplibregl.MapTouchEvent): number =>
      'touches' in ereignis.originalEvent ? ereignis.originalEvent.touches.length : 1;

    const beginn = (ereignis: maplibregl.MapMouseEvent | maplibregl.MapTouchEvent): void => {
      // Zwei Finger zoomen — das bleibt der Karte.
      if (fingerzahl(ereignis) > 1) {
        abbrechen();
        return;
      }
      const t = modulBei(ereignis.point);
      if (!t) return;
      ereignis.preventDefault();
      m.dragPan.disable();
      spur = [t];
      start = ereignis.point;
      zuletzt = ereignis.point;
      bewegt = false;
      mitFinger = 'touches' in ereignis.originalEvent;
      zeichne();
    };

    const bewegen = (ereignis: maplibregl.MapMouseEvent | maplibregl.MapTouchEvent): void => {
      if (!spur || !start || !zuletzt) return;
      if (fingerzahl(ereignis) > 1) {
        abbrechen();
        return;
      }
      ereignis.preventDefault();
      const p = ereignis.point;
      if (!bewegt && Math.hypot(p.x - start.x, p.y - start.y) > 3) bewegt = true;
      const strecke = Math.hypot(p.x - zuletzt.x, p.y - zuletzt.y);
      const schritte = Math.max(1, Math.ceil(strecke / 4));
      for (let i = 1; i <= schritte; i++) {
        const t = i / schritte;
        merke(new maplibregl.Point(zuletzt.x + (p.x - zuletzt.x) * t, zuletzt.y + (p.y - zuletzt.y) * t));
      }
      zuletzt = p;
    };

    const abbrechen = (): void => {
      if (!spur) return;
      spur = null;
      start = null;
      zuletzt = null;
      zeichne();
      m.dragPan.enable();
    };

    const ende = (): void => {
      if (!spur) return;
      const liste = spur;
      const warBewegt = bewegt;
      abbrechen();
      if (warBewegt) {
        stand.current.onModuleGewischt(liste.map((t) => ({ flaecheId: t.flaecheId, schluessel: t.schluessel })));
        return;
      }
      // Ein Tipp. Mit der Maus erledigt ihn der Klick. Mit dem Finger kommt
      // keiner, weil der Start hier abgefangen wurde — also gleich hier, und
      // ein dennoch folgender Klick wird kurz danach nicht mehr gezählt, sonst
      // schaltete er das Modul gleich wieder zurück.
      if (mitFinger && liste[0]) {
        fingertippAm.current = Date.now();
        stand.current.onModulGetippt(liste[0].flaecheId, liste[0].schluessel);
      }
    };

    m.on('mousedown', beginn);
    m.on('touchstart', beginn);
    m.on('mousemove', bewegen);
    m.on('touchmove', bewegen);
    m.on('mouseup', ende);
    m.on('touchend', ende);
    m.on('touchcancel', abbrechen);
    return () => {
      m.off('mousedown', beginn);
      m.off('touchstart', beginn);
      m.off('mousemove', bewegen);
      m.off('touchmove', bewegen);
      m.off('mouseup', ende);
      m.off('touchend', ende);
      m.off('touchcancel', abbrechen);
      abbrechen();
    };
  }, [bereit, modus]);

  useEffect(() => {
    const m = karte.current;
    if (!m || !bereit) return;
    m.setPaintProperty('wisch-fuell', 'fill-color', wischfarbe ?? '#ffffff');
    m.setPaintProperty('wisch-linie', 'line-color', wischfarbe ?? '#ffffff');
  }, [wischfarbe, bereit]);

  useEffect(() => {
    const m = karte.current;
    if (!m || !bereit) return;
    // Die Punkte werden immer gezeichnet, die Linie erst ab dem zweiten. Vorher
    // war nach dem ersten Klick nichts zu sehen — man wusste nicht, ob und wo
    // der Anfang gesetzt wurde.
    setzeDaten(m, 'messung', {
      type: 'FeatureCollection',
      features: [
        ...(messpunkte.length === 2
          ? [
              {
                type: 'Feature' as const,
                properties: {},
                geometry: {
                  type: 'LineString' as const,
                  coordinates: ringZuKoordinaten(messpunkte),
                },
              },
            ]
          : []),
        ...messpunkte.map((p, i) => ({
          type: 'Feature' as const,
          properties: { marke: i === 0 ? 'A' : 'B' },
          geometry: { type: 'Point' as const, coordinates: [p.lng, p.lat] },
        })),
      ],
    });
  }, [messpunkte, bereit]);

  // --- Das Mass an der Linie -----------------------------------------------
  //
  // Bisher stand die gemessene Laenge nur rechts im Flaechenpanel, neben dem
  // Referenzmass — und das Panel erscheint erst, wenn eine Flaeche gewaehlt
  // ist. Wer einfach etwas nachmessen wollte, klickte zweimal und sah keine
  // Zahl. Sie gehoert dorthin, wo gemessen wird.
  useEffect(() => {
    const m = karte.current;
    if (!m || !bereit) return;

    const zeichneMarke = (): void => {
      const [a, b] = messpunkte;
      if (!a || !b) {
        setMassmarke(null);
        return;
      }
      const eins = m.project([a.lng, a.lat]);
      const zwei = m.project([b.lng, b.lat]);
      const laenge = streckeM(a, b);
      setMassmarke({
        x: (eins.x + zwei.x) / 2,
        y: (eins.y + zwei.y) / 2,
        // Unter einem Meter sind Zentimeter die lesbarere Angabe.
        text: laenge < 1 ? `${(laenge * 100).toFixed(0)} cm` : `${laenge.toFixed(2)} m`,
      });
    };

    zeichneMarke();
    m.on('move', zeichneMarke);
    return () => {
      m.off('move', zeichneMarke);
    };
  }, [messpunkte, bereit]);

  // --- Bemaßungen und Markierungen -----------------------------------------
  //
  // Linien und Pfeilspitzen als Ebene, die Schilder als Elemente darüber (die
  // Karte hat keine Schriftquelle, siehe oben). Was gerade aufgezogen wird,
  // läuft durch denselben Weg — so sieht man schon beim Setzen, wie es wird.
  const alleMarken = useCallback((): Kartenmarke[] => {
    const liste = [...kartenmarken];
    if (entwurfsmarke && entwurf.length === 2 && (modus === 'bemassung' || modus === 'markierung')) {
      liste.push({
        id: 'entwurf',
        projekt_id: '',
        art: entwurfsmarke.art,
        punkte: entwurf,
        text: entwurfsmarke.text,
        seq: null,
      });
    }
    return liste.filter((marke) => marke.punkte.length >= 2);
  }, [kartenmarken, entwurfsmarke, entwurf, modus]);

  useEffect(() => {
    const m = karte.current;
    if (!m || !bereit) return;
    const marken = alleMarken();
    const mpp = meterJePixel(m.getCenter().lat, zoom);
    const linie = (punkte: Geo[], farbe: string): GeoJSON.Feature => ({
      type: 'Feature',
      properties: { farbe },
      geometry: { type: 'LineString', coordinates: ringZuKoordinaten(punkte) },
    });
    const features: GeoJSON.Feature[] = [];
    for (const marke of marken) {
      const [a, b] = marke.punkte as [Geo, Geo];
      const farbe = MARKENFARBEN[marke.art];
      if (marke.art === 'bemassung') {
        const [anfang, ende] = massendstriche(a, b, 14 * mpp);
        features.push(linie([a, b], farbe), linie(anfang, farbe), linie(ende, farbe));
      } else {
        // a ist die Pfeilspitze, b der Ort der Beschriftung.
        features.push(linie([b, a], farbe), linie(pfeilspitze(a, b, 16 * mpp), farbe));
      }
    }
    setzeDaten(m, 'kartenmarken', { type: 'FeatureCollection', features });
  }, [alleMarken, zoom, bereit]);

  useEffect(() => {
    const m = karte.current;
    if (!m || !bereit) return;
    const zeichneSchilder = (): void => {
      setSchilder(
        alleMarken().map((marke) => {
          const [a, b] = marke.punkte as [Geo, Geo];
          const ort =
            marke.art === 'bemassung'
              ? m.project([(a.lng + b.lng) / 2, (a.lat + b.lat) / 2])
              : m.project([b.lng, b.lat]);
          const amDach = marke.art === 'bemassung' ? dachlaenge(a, b) : null;
          const text =
            marke.art === 'bemassung'
              ? (amDach !== null ? `${masstext(amDach)} am Dach` : masstext(streckeM(a, b))) +
                (marke.text ? ` · ${marke.text}` : '')
              : marke.art === 'hinweis'
                ? marke.text || MARKENNAMEN.hinweis
                : marke.text
                  ? `${marke.art === 'wechselrichter' ? 'WR' : MARKENNAMEN[marke.art]}: ${marke.text}`
                  : MARKENNAMEN[marke.art];
          return { id: marke.id, x: ort.x, y: ort.y, text, farbe: MARKENFARBEN[marke.art] };
        }),
      );
    };
    zeichneSchilder();
    m.on('move', zeichneSchilder);
    return () => {
      m.off('move', zeichneSchilder);
    };
  }, [alleMarken, dachlaenge, bereit]);

  // Fuellt den Bereich aus, den der Aufrufer vorgibt. Kein eigener Rahmen —
  // sonst liegen zwei Behaelter ineinander und der innere bekommt keine Hoehe,
  // weil sein einziges Kind absolut positioniert ist.
  return (
    <div style={{ position: 'absolute', inset: 0 }}>
      <div ref={behaelter} style={{ position: 'absolute', inset: 0 }} />
      {massmarke && (
        // Die Marke liegt mitten im Klickbereich. Dass sie ihn nicht abfaengt,
        // haengt an "pointer-events: none" — sonst setzt der dritte Tipp keinen
        // neuen Anfangspunkt, weil er die Beschriftung trifft.
        <div className="massmarke" style={{ left: massmarke.x, top: massmarke.y }}>
          {massmarke.text}
        </div>
      )}
      {schilder.map((schild) => {
        // Antippen nur in der normalen Ansicht. Beim Zeichnen darf ein Schild
        // keinen Tipp abfangen, der mitten in ihm landet.
        const tippbar = markenWaehlbar && modus === 'ansehen' && schild.id !== 'entwurf';
        return (
          <div
            key={schild.id}
            className={`kartenschild${tippbar ? ' kartenschild--tippbar' : ''}`}
            style={{ left: schild.x, top: schild.y, borderColor: schild.farbe }}
            role={tippbar ? 'button' : undefined}
            tabIndex={tippbar ? 0 : undefined}
            title={tippbar ? 'Antippen zum Ändern oder Entfernen' : undefined}
            onClick={tippbar ? () => onKartenmarkeGetippt(schild.id) : undefined}
          >
            {schild.text}
          </div>
        );
      })}
      <div className="nachweis">
        {quelle.nachweis}
        {quelle.aufloesungCm ? ` · ${quelle.aufloesungCm} cm` : ''}
        {umrissquelle === 'bev' && ' · Umrisse: Kataster BEV (CC BY 4.0)'}
        {umrissquelle === 'osm' && ' · Umrisse: OpenStreetMap (ODbL)'}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Ebenen
// -----------------------------------------------------------------------------
function legeEbenenAn(m: maplibregl.Map): void {
  for (const name of [
    'gebaeude',
    'flaechen',
    'aussparungen',
    'sperrrand',
    'modulfelder',
    'traufkanten',
    'module',
    'module-entfernt',
    'stringlinien',
    'traeger',
    'schatten',
    'verschattungsobjekte',
    'entwurf',
    'messung',
    'referenzkante',
    'bearbeitung',
    'kartenmarken',
    'wisch',
  ]) {
    m.addSource(name, { type: 'geojson', data: leer });
  }

  m.addLayer({
    id: 'gebaeude-rand',
    type: 'line',
    source: 'gebaeude',
    paint: { 'line-color': '#50b0d8', 'line-width': 2, 'line-dasharray': [3, 2] },
  });

  m.addLayer({
    id: 'flaechen-fuell',
    type: 'fill',
    source: 'flaechen',
    paint: {
      'fill-color': ['case', ['get', 'aktiv'], '#1c9c71', '#ffffff'],
      'fill-opacity': ['case', ['get', 'aktiv'], 0.18, 0.08],
    },
  });
  m.addLayer({
    id: 'flaechen-rand',
    type: 'line',
    source: 'flaechen',
    paint: {
      'line-color': '#000000',
      'line-width': ['case', ['get', 'aktiv'], 2.5, 1.5],
    },
  });
  // Der Sicherheitsabstand um Aussparungen — gestrichelt, damit sichtbar ist,
  // warum dort mehr frei bleibt als gezeichnet.
  m.addLayer({
    id: 'sperrrand-linie',
    type: 'line',
    source: 'sperrrand',
    paint: { 'line-color': '#d86038', 'line-width': 2, 'line-dasharray': [2, 1.5] },
  });
  m.addLayer({
    id: 'aussparungen-fuell',
    type: 'fill',
    source: 'aussparungen',
    paint: { 'fill-color': '#d86038', 'fill-opacity': 0.35 },
  });
  m.addLayer({
    id: 'aussparungen-rand',
    type: 'line',
    source: 'aussparungen',
    // Das gewaehlte orange und kraeftig — wie die angetippte Ecke.
    paint: {
      'line-color': ['case', ['get', 'gewaehlt'], '#f08a24', '#d86038'],
      'line-width': ['case', ['get', 'gewaehlt'], 4, 1.5],
    },
  });
  // Das Modulfeld: gelb gestrichelt wie eine Markierung auf dem Dach — nicht
  // grün, das ist die gewählte Fläche, und nicht orange, das sind Hindernisse.
  m.addLayer({
    id: 'modulfelder-rand',
    type: 'line',
    source: 'modulfelder',
    paint: {
      'line-color': '#ffd23f',
      'line-width': ['case', ['get', 'aktiv'], 3, 1.5],
      'line-opacity': ['case', ['get', 'aktiv'], 1, 0.6],
      'line-dasharray': [3, 1.5],
    },
  });
  m.addLayer({
    id: 'traufkanten',
    type: 'line',
    source: 'traufkanten',
    paint: { 'line-color': '#d86038', 'line-width': 5 },
  });
  // Entfernte Plaetze: sichtbar, aber offensichtlich leer. Sie liegen unter den
  // gesetzten Modulen, damit bei Ueberschneidung immer das echte Modul gewinnt.
  m.addLayer({
    id: 'module-entfernt-fuell',
    type: 'fill',
    source: 'module-entfernt',
    paint: { 'fill-color': '#ffffff', 'fill-opacity': 0.12 },
  });
  m.addLayer({
    id: 'module-entfernt-rand',
    type: 'line',
    source: 'module-entfernt',
    paint: {
      'line-color': '#ffffff',
      'line-width': 1.2,
      'line-dasharray': [2, 2],
      'line-opacity': 0.8,
    },
  });
  m.addLayer({
    id: 'module-fuell',
    type: 'fill',
    source: 'module',
    paint: { 'fill-color': ['get', 'farbe'], 'fill-opacity': 0.8 },
  });
  m.addLayer({
    id: 'module-rand',
    type: 'line',
    source: 'module',
    paint: { 'line-color': '#ffffff', 'line-width': 0.6 },
  });
  // Sparren und Pfetten ueber den Modulen, aber durchscheinend: Man soll sehen,
  // wo ein Haken hinkommt, ohne dass das Feld darunter verschwindet. Sparren
  // weiss gestrichelt, Pfetten blau durchgezogen — gleich gezeichnet waren die
  // Pfetten im Gitter der Sparren nicht zu finden.
  m.addLayer({
    id: 'traeger-sparren',
    type: 'line',
    source: 'traeger',
    filter: ['==', ['get', 'art'], 'sparren'],
    paint: { 'line-color': '#ffffff', 'line-opacity': 0.6, 'line-width': 1.5, 'line-dasharray': [4, 2] },
  });
  m.addLayer({
    id: 'traeger-pfetten',
    type: 'line',
    source: 'traeger',
    filter: ['==', ['get', 'art'], 'pfetten'],
    paint: { 'line-color': '#3fb4ff', 'line-opacity': 0.8, 'line-width': 2.5 },
  });
  // Der Schatten liegt unter allem anderen — er ist Hintergrund, keine Eingabe.
  m.addLayer({
    id: 'schatten-fuell',
    type: 'fill',
    source: 'schatten',
    paint: { 'fill-color': '#282828', 'fill-opacity': 0.35 },
  });
  m.addLayer({
    id: 'verschattungsobjekte-fuell',
    type: 'fill',
    source: 'verschattungsobjekte',
    paint: { 'fill-color': '#3aa66f', 'fill-opacity': 0.5 },
  });
  m.addLayer({
    id: 'verschattungsobjekte-rand',
    type: 'line',
    source: 'verschattungsobjekte',
    paint: { 'line-color': '#3aa66f', 'line-width': 1.5 },
  });
  // Die Spur beim Wischen: was beim Loslassen in den String kommt. Weiß
  // umrandet, damit sie auch auf einem Modul derselben Farbe auffällt.
  m.addLayer({
    id: 'wisch-fuell',
    type: 'fill',
    source: 'wisch',
    filter: ['==', ['geometry-type'], 'Polygon'],
    paint: { 'fill-color': '#ffffff', 'fill-opacity': 0.9, 'fill-outline-color': '#ffffff' },
  });
  m.addLayer({
    id: 'wisch-linie',
    type: 'line',
    source: 'wisch',
    filter: ['==', ['geometry-type'], 'LineString'],
    paint: { 'line-color': '#ffffff', 'line-width': 4 },
    layout: { 'line-cap': 'round', 'line-join': 'round' },
  });
  m.addLayer({
    id: 'stringlinien',
    type: 'line',
    source: 'stringlinien',
    paint: {
      'line-color': ['get', 'farbe'],
      'line-width': 2.5,
      'line-opacity': 0.95,
    },
    layout: { 'line-cap': 'round', 'line-join': 'round' },
  });
  m.addLayer({
    id: 'entwurf-linie',
    type: 'line',
    source: 'entwurf',
    filter: ['==', ['geometry-type'], 'LineString'],
    paint: { 'line-color': '#000000', 'line-width': 2, 'line-dasharray': [2, 1.5] },
  });
  m.addLayer({
    id: 'entwurf-punkte',
    type: 'circle',
    source: 'entwurf',
    filter: ['==', ['geometry-type'], 'Point'],
    paint: {
      'circle-radius': 6,
      'circle-color': '#ffffff',
      'circle-stroke-color': '#000000',
      'circle-stroke-width': 2,
    },
  });
  // Die Kante, auf die eingemessen wird. Kraeftiges Gruen mit weisser Fassung:
  // Die Traufkante ist schon orange, und beide gleichzeitig zu sehen muss
  // eindeutig bleiben. Vorher war ueberhaupt nicht zu erkennen, welche Kante
  // in der Liste gerade gewaehlt war.
  m.addLayer({
    id: 'referenzkante-fassung',
    type: 'line',
    source: 'referenzkante',
    paint: { 'line-color': '#ffffff', 'line-width': 9, 'line-opacity': 0.9 },
    layout: { 'line-cap': 'round' },
  });
  m.addLayer({
    id: 'referenzkante',
    type: 'line',
    source: 'referenzkante',
    paint: { 'line-color': '#00b050', 'line-width': 5 },
    layout: { 'line-cap': 'round' },
  });

  // Ecken zum Ziehen. Die Mittelpunkte der Kanten sind kleiner und hohl — ein
  // Tipp darauf setzt dort eine neue Ecke.
  m.addLayer({
    id: 'bearbeitung-linie',
    type: 'line',
    source: 'bearbeitung',
    filter: ['==', ['geometry-type'], 'LineString'],
    paint: { 'line-color': '#00b050', 'line-width': 2 },
  });
  m.addLayer({
    id: 'bearbeitung-mitten',
    type: 'circle',
    source: 'bearbeitung',
    filter: ['==', ['get', 'art'], 'mitte'],
    paint: {
      'circle-radius': 5,
      'circle-color': '#ffffff',
      'circle-opacity': 0.65,
      'circle-stroke-color': '#00b050',
      'circle-stroke-width': 2,
    },
  });
  m.addLayer({
    id: 'bearbeitung-ecken',
    type: 'circle',
    source: 'bearbeitung',
    filter: ['==', ['get', 'art'], 'ecke'],
    paint: {
      'circle-radius': ['case', ['get', 'gewaehlt'], 12, 9],
      'circle-color': ['case', ['get', 'gewaehlt'], '#d9822b', '#00b050'],
      'circle-stroke-color': '#ffffff',
      'circle-stroke-width': 3,
    },
  });

  m.addLayer({
    id: 'kartenmarken-fassung',
    type: 'line',
    source: 'kartenmarken',
    paint: { 'line-color': '#15181e', 'line-width': 5, 'line-opacity': 0.55 },
    layout: { 'line-cap': 'round', 'line-join': 'round' },
  });
  m.addLayer({
    id: 'kartenmarken',
    type: 'line',
    source: 'kartenmarken',
    paint: { 'line-color': ['get', 'farbe'], 'line-width': 2.5 },
    layout: { 'line-cap': 'round', 'line-join': 'round' },
  });

  m.addLayer({
    id: 'messung-linie',
    type: 'line',
    source: 'messung',
    filter: ['==', ['geometry-type'], 'LineString'],
    paint: { 'line-color': '#2f86c8', 'line-width': 3 },
  });
  m.addLayer({
    id: 'messung-punkte',
    type: 'circle',
    source: 'messung',
    filter: ['==', ['geometry-type'], 'Point'],
    paint: {
      // Anfang voll, Ende hohl — der Stil kennt keine Schrift, also wird die
      // Unterscheidung gezeichnet statt beschriftet.
      'circle-radius': 7,
      'circle-color': ['case', ['==', ['get', 'marke'], 'A'], '#2f86c8', '#ffffff'],
      'circle-stroke-color': '#2f86c8',
      'circle-stroke-width': 3,
    },
  });
}

function setzeDaten(m: maplibregl.Map, name: string, daten: GeoJSON.FeatureCollection): void {
  const quelle = m.getSource(name) as maplibregl.GeoJSONSource | undefined;
  quelle?.setData(daten);
  const stand = QUELLDATEN.get(m) ?? new Map<string, GeoJSON.FeatureCollection>();
  stand.set(name, daten);
  QUELLDATEN.set(m, stand);
}

/**
 * Die nächste Ecke einer Fläche oder Aussparung im Fangradius — oder nichts.
 * Gerechnet am Schirm, damit der Fang bei jeder Zoomstufe gleich weit greift.
 */
function fangeEcke(m: maplibregl.Map, punkt: maplibregl.Point, flaechen: Flaeche[]): Geo | null {
  const r = fangRadius();
  let beste: Geo | null = null;
  let besterAbstand = r;
  for (const flaeche of flaechen) {
    for (const ring of [flaeche.polygon, ...(flaeche.aussparungen ?? [])]) {
      for (const ecke of ring) {
        const p = m.project([ecke.lng, ecke.lat]);
        const d = Math.hypot(p.x - punkt.x, p.y - punkt.y);
        if (d <= besterAbstand) {
          besterAbstand = d;
          beste = ecke;
        }
      }
    }
  }
  return beste;
}

const ringZuKoordinaten = (ring: Geo[]): [number, number][] =>
  ring.map((p) => [p.lng, p.lat] as [number, number]);
